import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Error as ErrorIcon, CheckCircle, Pending, Warning, ArrowBack, Schedule } from '@mui/icons-material';
import { ReceivedItemVm, ReceptionItemStatus } from 'api/reception/received-item.vm';
import { getReceivedItems } from 'api/reception/get-received-items';
import { ReceptionVm } from 'api/reception/reception.vm';
import { getReception } from 'api/reception/get-reception';

const ReceptionProcessPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { receptionId } = useParams<{ receptionId: string }>();
  const [items, setItems] = useState<ReceivedItemVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [receptionDetails, setReceptionDetails] = useState<ReceptionVm | null>(null);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchReceivedItems = (offset: string | null, reset: boolean = false) => {
    if (!receptionId) return;

    setLoading(true);

    getReceivedItems(receptionId, offset || '', {
      success: (response) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setItems(reset ? response.objects : [...items, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter itens recebidos:', err);
        setLoading(false);
      },
    });
  };

  const fetchReceptionDetails = () => {
    if (!receptionId) return;

    setLoadingDetails(true);

    getReception(receptionId, {
      success: (response) => {
        setReceptionDetails(response);
        setLoadingDetails(false);
      },
      error: (err) => {
        console.error('Falha ao obter detalhes da receção:', err);
        setLoadingDetails(false);
      },
    });
  };

  useEffect(() => {
    fetchReceivedItems(null, true);
    fetchReceptionDetails();
  }, [receptionId]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchReceivedItems(nextOffset);
  };

  const getStatusChip = (status: ReceptionItemStatus) => {
    switch (status) {
      case ReceptionItemStatus.RECEIVED:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Recebido" color="success" size="small" />;
      case ReceptionItemStatus.PENDING_INTEGRATION:
        return <Chip icon={<Pending fontSize="small" />} label="Pendente Integração" color="warning" size="small" />;
      case ReceptionItemStatus.INTEGRATED:
        return <Chip icon={<CheckCircle fontSize="small" />} label="Integrado" color="success" size="small" />;
      case ReceptionItemStatus.INTEGRATION_ERROR:
        return <Chip icon={<ErrorIcon fontSize="small" />} label="Erro de Integração" color="error" size="small" />;
      case ReceptionItemStatus.CANCELED:
        return <Chip icon={<Warning fontSize="small" />} label="Cancelado" color="default" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  const getReceptionStatusChip = (status: any) => {
    switch (status) {
      case 'OPEN':
        return <Chip icon={<Schedule fontSize="small" />} label="Aberta" color="warning" size="small" />;
      case 'CLOSED':
        return <Chip icon={<CheckCircle fontSize="small" />} label="Fechada" color="success" size="small" />;
      case 'CLOSED_WITH_ERROR':
        return <Chip icon={<ErrorIcon fontSize="small" />} label="Fechada com Erro" color="error" size="small" />;
      case 'PENDING_INTEGRATION':
        return <Chip icon={<Pending fontSize="small" />} label="Pendente Integração" color="warning" size="small" />;
      case 'CANCELED':
        return <Chip icon={<Warning fontSize="small" />} label="Cancelada" color="default" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation}>
            Processos de Receção
          </Link>
          <Typography color="text.primary">Receção #{receptionId}</Typography>
        </Breadcrumbs>
      </Box>

      {loadingDetails ? (
        <CircularProgress />
      ) : receptionDetails ? (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes da Receção
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                ID
              </Typography>
              <Typography variant="body1">{receptionDetails.id}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Fornecedor
              </Typography>
              <Typography variant="body1">{receptionDetails.supplierName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Estado
              </Typography>
              {getReceptionStatusChip(receptionDetails.status)}
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Data de Criação
              </Typography>
              <Typography variant="body1">{formatDate(receptionDetails.createdAt)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Recetor
              </Typography>
              <Typography variant="body1">{receptionDetails.receiverName || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Tipo
              </Typography>
              <Typography variant="body1">{receptionDetails.type === 'BY_SUPPLIER' ? 'Por Fornecedor' : 'Por Encomenda'}</Typography>
            </Box>
          </Box>
          {receptionDetails.thirdPartyReferenceDocument && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Referência
              </Typography>
              <Typography variant="body1">{receptionDetails.thirdPartyReferenceDocument}</Typography>
            </Box>
          )}
          {receptionDetails.integrationError && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Erro de Integração
              </Typography>
              <Typography variant="body1" color="error">
                {receptionDetails.integrationError}
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        <Typography variant="body2" color="error">
          Falha ao carregar detalhes da receção
        </Typography>
      )}

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Itens Recebidos
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>Produto</TableCell>
              <TableCell>Linha de Encomenda</TableCell>
              <TableCell>Lote</TableCell>
              <TableCell>Número de Série</TableCell>
              <TableCell>Recetor</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Erro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={`${item.orderItemId}-${index}`} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {item.productName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {item.productSku}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{item.orderItemId}</TableCell>
                <TableCell>{item.batch}</TableCell>
                <TableCell>{item.serialNumber}</TableCell>
                <TableCell>{item.receiverName}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{getStatusChip(item.status)}</TableCell>
                <TableCell>{item.integrationError || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : items.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais itens para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum item encontrado para esta receção
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ReceptionProcessPage;
